/**
 * Bug Report Capture Service
 * Auto-captures technical data for bug reports
 */
import html2canvas from 'html2canvas';
import activityTracker from './userActivityTracker';

class BugReportCapture {
  constructor() {
    this.consoleErrors = [];
    this.networkLogs = [];
    this.maxConsoleLogs = 20;
    this.maxNetworkLogs = 20;
    
    // Start capturing console errors
    this.captureConsoleErrors();
    
    // Start capturing network requests
    this.captureNetworkRequests();
  }

  /**
   * Capture console errors and warnings
   */
  captureConsoleErrors() {
    const originalError = console.error;
    const originalWarn = console.warn;
    
    console.error = (...args) => {
      this.addConsoleLog('error', args);
      originalError.apply(console, args);
    };
    
    console.warn = (...args) => {
      this.addConsoleLog('warn', args);
      originalWarn.apply(console, args);
    };
    
    // Capture unhandled errors
    window.addEventListener('error', (event) => {
      this.addConsoleLog('error', [event.message, event.filename, event.lineno, event.colno]);
    });
    
    // Capture unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.addConsoleLog('error', ['Unhandled Promise Rejection:', event.reason]);
    });
  }

  /**
   * Add console log entry
   */
  addConsoleLog(level, args) {
    const entry = {
      timestamp: new Date().toISOString(),
      level,
      message: args.map(arg => {
        if (typeof arg === 'object') {
          try {
            return JSON.stringify(arg, null, 2);
          } catch (e) {
            return String(arg);
          }
        }
        return String(arg);
      }).join(' ')
    };
    
    this.consoleErrors.unshift(entry);
    
    if (this.consoleErrors.length > this.maxConsoleLogs) {
      this.consoleErrors = this.consoleErrors.slice(0, this.maxConsoleLogs);
    }
  }

  /**
   * Capture network requests (fetch and axios)
   */
  captureNetworkRequests() {
    // Intercept fetch
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const startTime = Date.now();
      const url = args[0];
      const options = args[1] || {};
      
      try {
        const response = await originalFetch(...args);
        const duration = Date.now() - startTime;
        
        this.addNetworkLog({
          url,
          method: options.method || 'GET',
          status: response.status,
          duration,
          error: null
        });
        
        return response;
      } catch (error) {
        const duration = Date.now() - startTime;
        
        this.addNetworkLog({
          url,
          method: options.method || 'GET',
          status: 0,
          duration,
          error: error.message
        });
        
        throw error;
      }
    };
  }

  /**
   * Add network log entry
   */
  addNetworkLog(entry) {
    const log = {
      timestamp: new Date().toISOString(),
      ...entry
    };
    
    this.networkLogs.unshift(log);
    
    if (this.networkLogs.length > this.maxNetworkLogs) {
      this.networkLogs = this.networkLogs.slice(0, this.maxNetworkLogs);
    }
  }

  /**
   * Capture screenshot of current page
   * Hides the Bug Report Modal, Help Widget, and debugging elements
   */
  async captureScreenshot() {
    try {
      // Hide bug report modal, help widget, and any debugging overlays
      const elementsToHide = [
        document.querySelector('[data-bug-report-modal="true"]'),
        document.querySelector('[data-bug-report-overlay="true"]'),
        document.querySelector('[data-help-sheet="true"]'),
        // Hide rrweb and visual edit elements
        ...document.querySelectorAll('[class*="rrweb"]'),
        ...document.querySelectorAll('[id*="rrweb"]'),
        ...document.querySelectorAll('[class*="visual-edit"]'),
        ...document.querySelectorAll('[data-visual-edit]'),
      ].filter(Boolean);
      
      // Store original styles and hide elements
      const originalStyles = elementsToHide.map(el => {
        const original = {
          visibility: el.style.visibility,
          display: el.style.display,
          opacity: el.style.opacity
        };
        el.style.visibility = 'hidden';
        el.style.opacity = '0';
        return { el, original };
      });
      
      // Small delay to ensure DOM updates
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Use window.devicePixelRatio for crisp screenshots on high-DPI displays
      const pixelRatio = window.devicePixelRatio || 1;
      
      const canvas = await html2canvas(document.body, {
        allowTaint: true,
        useCORS: true,
        logging: false,
        scale: Math.max(pixelRatio, 2), // At least 2x for good quality
        backgroundColor: '#ffffff',
        removeContainer: true,
        foreignObjectRendering: false, // Disable foreignObject to avoid rendering issues
        imageTimeout: 15000,
        onclone: (clonedDoc) => {
          // Remove any elements that might cause grey boxes in the clone
          const elementsToRemove = clonedDoc.querySelectorAll(
            '[class*="rrweb"], [id*="rrweb"], [class*="visual-edit"], ' +
            '[data-visual-edit], [data-radix-popper-content-wrapper], ' +
            '[data-radix-portal], .fixed.inset-0.z-50'
          );
          elementsToRemove.forEach(el => el.remove());
        },
        ignoreElements: (element) => {
          // Skip certain elements entirely
          const tagName = element.tagName?.toLowerCase();
          const className = element.className?.toString() || '';
          const id = element.id || '';
          
          // Skip script and style tags
          if (tagName === 'script' || tagName === 'noscript' || tagName === 'iframe') return true;
          
          // Skip rrweb elements
          if (className.includes('rrweb') || id.includes('rrweb')) return true;
          
          // Skip visual edit elements
          if (className.includes('visual-edit') || element.hasAttribute?.('data-visual-edit')) return true;
          
          // Skip Radix UI portals (dropdown menus, tooltips, etc)
          if (element.hasAttribute?.('data-radix-portal')) return true;
          if (element.hasAttribute?.('data-radix-popper-content-wrapper')) return true;
          
          // Skip bug report and help elements
          if (element.getAttribute?.('data-bug-report-modal') === 'true') return true;
          if (element.getAttribute?.('data-bug-report-overlay') === 'true') return true;
          if (element.getAttribute?.('data-help-sheet') === 'true') return true;
          
          return false;
        }
      });
      
      // Restore visibility
      originalStyles.forEach(({ el, original }) => {
        el.style.visibility = original.visibility || '';
        el.style.opacity = original.opacity || '';
      });
      
      return canvas.toDataURL('image/png', 1.0);
    } catch (error) {
      console.error('Failed to capture screenshot:', error);
      return null;
    }
  }

  /**
   * Get browser and device information
   */
  getBrowserInfo() {
    const ua = navigator.userAgent;
    
    return {
      userAgent: ua,
      browser: this.detectBrowser(ua),
      os: this.detectOS(ua),
      device: this.detectDevice(),
      screen: `${window.screen.width}x${window.screen.height}`,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      language: navigator.language,
      platform: navigator.platform
    };
  }

  detectBrowser(ua) {
    if (ua.includes('Chrome')) return `Chrome ${this.extractVersion(ua, 'Chrome/')}`;
    if (ua.includes('Firefox')) return `Firefox ${this.extractVersion(ua, 'Firefox/')}`;
    if (ua.includes('Safari') && !ua.includes('Chrome')) return `Safari ${this.extractVersion(ua, 'Version/')}`;
    if (ua.includes('Edge')) return `Edge ${this.extractVersion(ua, 'Edge/')}`;
    return 'Unknown';
  }

  detectOS(ua) {
    if (ua.includes('Windows')) return 'Windows';
    if (ua.includes('Mac OS')) return 'macOS';
    if (ua.includes('Linux')) return 'Linux';
    if (ua.includes('Android')) return 'Android';
    if (ua.includes('iOS')) return 'iOS';
    return 'Unknown';
  }

  detectDevice() {
    const width = window.innerWidth;
    if (width < 768) return 'Mobile';
    if (width < 1024) return 'Tablet';
    return 'Desktop';
  }

  extractVersion(ua, prefix) {
    const index = ua.indexOf(prefix);
    if (index === -1) return '';
    const version = ua.substring(index + prefix.length).split(' ')[0];
    return version;
  }

  /**
   * Get current page context
   */
  getPageContext() {
    return {
      url: window.location.href,
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash,
      referrer: document.referrer,
      title: document.title
    };
  }

  /**
   * Get user context from localStorage
   */
  getUserContext() {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return null;
      
      // Decode JWT (basic decode, not verification)
      const payload = JSON.parse(atob(token.split('.')[1]));
      
      return {
        user_id: payload.user_id,
        email: payload.email,
        role: payload.role,
        company_id: payload.company_id
      };
    } catch (error) {
      return null;
    }
  }

  /**
   * Compile all bug report data
   */
  async compileReportData() {
    const screenshot = await this.captureScreenshot();
    
    return {
      user_steps: activityTracker.getRecentSteps(),
      current_page: this.getPageContext(),
      user_context: this.getUserContext(),
      environment: this.getBrowserInfo(),
      console_errors: [...this.consoleErrors].slice(0, 10),
      api_calls: [...this.networkLogs].filter(log => 
        log.url.includes('/api/')
      ).slice(0, 10),
      screenshot_data: screenshot,
      captured_at: new Date().toISOString()
    };
  }

  /**
   * Get console errors in readable format
   */
  getFormattedConsoleErrors() {
    return this.consoleErrors.slice(0, 10).map(log => ({
      timestamp: new Date(log.timestamp).toLocaleTimeString(),
      level: log.level.toUpperCase(),
      message: log.message.substring(0, 500)
    }));
  }

  /**
   * Get network logs in readable format
   */
  getFormattedNetworkLogs() {
    return this.networkLogs
      .filter(log => log.url.includes('/api/'))
      .slice(0, 10)
      .map(log => ({
        timestamp: new Date(log.timestamp).toLocaleTimeString(),
        method: log.method,
        endpoint: this.extractEndpoint(log.url),
        status: log.status,
        duration: `${log.duration}ms`,
        error: log.error
      }));
  }

  extractEndpoint(url) {
    try {
      const urlObj = new URL(url);
      return urlObj.pathname;
    } catch (e) {
      return url;
    }
  }
}

// Create singleton instance
const bugReportCapture = new BugReportCapture();

export default bugReportCapture;
